import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Repeat2, Copy, Check, Hash } from "lucide-react";

const platformColor = {
  "TikTok": "text-white border-white/20",
  "YouTube Shorts": "text-brand border-brand/30",
  "Instagram Reels": "text-signal-blue border-signal-blue/30",
};

function CopyMini({ text }) {
  const [done, setDone] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setDone(true); toast.success("Copied"); setTimeout(() => setDone(false), 1200); }}
      className="text-white/40 hover:text-white transition-colors shrink-0">
      {done ? <Check className="w-3.5 h-3.5 text-signal-green" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

export default function RepurposePanel({ pkg, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const platforms = pkg.repurpose || [];

  const run = async () => {
    setLoading(true);
    try {
      const r = await api.post("/content/repurpose", { package_id: pkg.id });
      onUpdate(r.data.platforms);
      toast.success("Repurposed for short-form platforms");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Repurpose failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-surface border border-white/10 rounded-md p-5" data-testid="repurpose-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-mono flex items-center gap-2">
          <Repeat2 className="w-3.5 h-3.5" /> Cross-Platform Repurpose
        </div>
        <button data-testid="repurpose-btn" onClick={run} disabled={loading}
          className="text-xs font-mono text-brand hover:text-brand-hover disabled:opacity-50 transition-colors">
          {loading ? "generating..." : platforms.length ? "regenerate" : "generate variants"}
        </button>
      </div>

      {platforms.length === 0 ? (
        <p className="text-white/40 text-sm">Turn this video into ready-to-post TikTok, Shorts & Reels variants — hooks, captions, hashtags & on-screen text.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {platforms.map((p, i) => (
            <div key={p.platform || i} className={`border rounded-md p-4 bg-[#0A0A0A] ${platformColor[p.platform] || "border-white/15"}`} data-testid={`repurpose-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="font-heading text-sm">{p.platform}</span>
                <span className="text-[10px] font-mono text-white/40">{p.duration}</span>
              </div>
              <div className="space-y-3 text-sm">
                <div>
                  <div className="flex items-center justify-between"><span className="text-[10px] uppercase tracking-wider text-white/30 font-mono">Hook</span><CopyMini text={p.hook} /></div>
                  <p className="text-white/80 italic mt-1">"{p.hook}"</p>
                </div>
                <div>
                  <div className="flex items-center justify-between"><span className="text-[10px] uppercase tracking-wider text-white/30 font-mono">Caption</span><CopyMini text={p.caption} /></div>
                  <p className="text-white/60 mt-1 text-xs leading-relaxed">{p.caption}</p>
                </div>
                {(p.on_screen_text || []).length > 0 && (
                  <div>
                    <span className="text-[10px] uppercase tracking-wider text-white/30 font-mono">On-Screen</span>
                    <ul className="mt-1 space-y-0.5">
                      {p.on_screen_text.map((t, j) => <li key={`${p.platform}-os-${j}`} className="text-xs text-white/55">{j + 1}. {t}</li>)}
                    </ul>
                  </div>
                )}
                <div className="flex flex-wrap gap-1 pt-1 border-t border-white/5">
                  {(p.hashtags || []).map((h, j) => (
                    <span key={`${p.platform}-tag-${j}`} className="text-[10px] font-mono text-white/40 flex items-center"><Hash className="w-2.5 h-2.5" />{h.replace(/^#/, "")}</span>
                  ))}
                </div>
                <div className="text-[10px] font-mono text-signal-green/80">⏰ {p.best_time}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
